import { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { InfoOutlined, SearchOutlined } from "@mui/icons-material";
import { AdminFolderAccess } from "types/interfaces/adminUsers";
import CustomScrollbars from "../CustomScrollbars";
import {
  useGetAdminUserFoldersToShareQuery,
  useGrantFolderAccessMutation,
  useRevokeFolderAccessMutation,
} from "features/adminUsers/adminUsers";

interface Props {
  open: boolean;
  onClose: () => void;
  userId: number;
  canEdit: boolean;
  onSuccess: () => void;
  onError: (message: string) => void;
}

function getFolderDisplayName(folder: AdminFolderAccess): string {
  if (folder.folder_display != null && folder.folder_display !== "") {
    return folder.folder_display;
  }
  const isMigration = !folder.is_integration && !folder.is_sftp_folder;
  const ownerName = (folder.owner_first_name || "").trim();
  if (isMigration && ownerName) {
    return `${ownerName} - ${folder.folder}`;
  }
  return folder.folder;
}

function FolderRow({
  folder,
  displayName,
  checked,
  onToggle,
  disabled,
}: {
  folder: AdminFolderAccess;
  displayName: string;
  checked: boolean;
  onToggle: (id: number) => void;
  disabled?: boolean;
}) {
  const checkbox = (
    <Checkbox
      checked={checked}
      size="small"
      disableRipple
      disabled={disabled}
      sx={{ p: 0, flexShrink: 0 }}
    />
  );

  return (
    <Box
      display="flex"
      alignItems="center"
      gap={1}
      py={0.6}
      px={0.5}
      sx={{
        borderRadius: 1,
        cursor: disabled ? "default" : "pointer",
        "&:hover": disabled ? undefined : { bgcolor: "rgba(0,0,0,0.03)" },
      }}
      onClick={() => !disabled && onToggle(folder.id)}
    >
      {disabled ? (
        <Tooltip
          title="User &amp; Folder Manager or Super Admin required to change folder access."
          placement="left"
        >
          <span>{checkbox}</span>
        </Tooltip>
      ) : (
        checkbox
      )}
      <Box flex={1} minWidth={0}>
        <Typography variant="body2" fontWeight={600} noWrap>
          {displayName}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap>
          {folder.folder_prefix}
        </Typography>
      </Box>
      <Box display="flex" gap={0.5} flexShrink={0}>
        {folder.is_sftp_folder ? (
          <Chip
            label="SFTP"
            size="small"
            sx={{
              height: 16,
              fontSize: 10,
              fontWeight: 700,
              bgcolor: "rgba(21,101,192,0.10)",
              color: "#1565c0",
            }}
          />
        ) : folder.is_integration ? (
          <Chip
            label="Integration"
            size="small"
            sx={{ height: 16, fontSize: 10, fontWeight: 700, bgcolor: "#fff3e0", color: "#e65100" }}
          />
        ) : (
          <Chip
            label="Migration folder"
            size="small"
            sx={{
              height: 16,
              fontSize: 10,
              fontWeight: 700,
              bgcolor: "#fff8e1",
              color: "#ff8f00",
            }}
          />
        )}
      </Box>
    </Box>
  );
}

export default function ShareFolderAccessModal({
  open,
  onClose,
  userId,
  canEdit,
  onSuccess,
  onError,
}: Props) {
  const { data: folders, isLoading } = useGetAdminUserFoldersToShareQuery(userId, { skip: !open });
  const [grant] = useGrantFolderAccessMutation();
  const [revoke] = useRevokeFolderAccessMutation();

  const [pendingState, setPendingState] = useState<Record<number, boolean>>({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [search, setSearch] = useState("");

  const list = folders ?? [];
  const originalState = Object.fromEntries(
    list.map((f) => [f.id, f.has_access === true])
  ) as Record<number, boolean>;

  const handleToggle = (id: number) => {
    const original = originalState[id];
    setPendingState((prev) => {
      const hasPending = id in prev;
      if (hasPending) {
        const next = { ...prev };
        delete next[id];
        return next;
      }
      return { ...prev, [id]: !original };
    });
  };

  const hasPendingChanges = Object.keys(pendingState).length > 0;

  const filtered = search.trim()
    ? list.filter(
        (f) =>
          getFolderDisplayName(f).toLowerCase().includes(search.toLowerCase()) ||
          f.folder_prefix.toLowerCase().includes(search.toLowerCase())
      )
    : list;

  const sorted = [...filtered].sort((a, b) => {
    const aChecked = a.id in pendingState ? !!pendingState[a.id] : a.has_access === true;
    const bChecked = b.id in pendingState ? !!pendingState[b.id] : b.has_access === true;
    if (aChecked !== bChecked) return aChecked ? -1 : 1;
    return getFolderDisplayName(a).localeCompare(getFolderDisplayName(b), undefined, {
      sensitivity: "base",
    });
  });

  const handleSave = () => setConfirmOpen(true);

  const handleConfirm = async () => {
    setIsSaving(true);
    setSaveError(false);
    try {
      const promises = list
        .filter((f) => f.id in pendingState)
        .map((f) =>
          pendingState[f.id]
            ? grant({ userId, folderId: f.id }).unwrap()
            : revoke({ userId, folderId: f.id }).unwrap()
        );
      await Promise.all(promises);
      setPendingState({});
      setConfirmOpen(false);
      onSuccess();
    } catch {
      setSaveError(true);
      onError("Failed to save folder access changes.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelConfirm = () => {
    setConfirmOpen(false);
    setSaveError(false);
  };

  const toGrant = list.filter((f) => pendingState[f.id] === true && originalState[f.id] === false);
  const toRevoke = list.filter((f) => pendingState[f.id] === false && originalState[f.id] === true);

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
        <DialogTitle sx={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 0.5 }}>
          Share Folder Access
          <Tooltip title="If you check a folder, the user will see it on the web platform in their file storage.">
            <InfoOutlined sx={{ fontSize: 18, color: "text.secondary" }} />
          </Tooltip>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Check a folder to give this user access; they will see it on the web platform. Only
            folders that do not belong to this user are listed.
          </Typography>
          {isLoading ? (
            <Box>
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} variant="text" height={40} sx={{ mb: 0.5 }} />
              ))}
            </Box>
          ) : (
            <>
              <TextField
                size="small"
                placeholder="Search folders..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                fullWidth
                sx={{ mb: 1, "& .MuiOutlinedInput-root": { borderRadius: 1.5, fontSize: 13 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchOutlined sx={{ fontSize: 16, color: "text.disabled" }} />
                    </InputAdornment>
                  ),
                }}
              />
              <CustomScrollbars sx={{ maxHeight: 360 }}>
                {sorted.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                    No folders match your search.
                  </Typography>
                ) : (
                  sorted.map((folder) => (
                    <FolderRow
                      key={folder.id}
                      folder={folder}
                      displayName={getFolderDisplayName(folder)}
                      checked={folder.id in pendingState ? !!pendingState[folder.id] : folder.has_access === true}
                      onToggle={handleToggle}
                      disabled={!canEdit}
                    />
                  ))
                )}
              </CustomScrollbars>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} sx={{ textTransform: "none" }}>
            Close
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!canEdit || !hasPendingChanges || isLoading}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmOpen} onClose={handleCancelConfirm} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Confirm Access Changes</DialogTitle>
        <DialogContent dividers>
          {toGrant.length > 0 && (
            <Box mb={toRevoke.length > 0 ? 2 : 0}>
              <Typography
                variant="caption"
                fontWeight={700}
                color="success.main"
                textTransform="uppercase"
                display="block"
                mb={0.75}
              >
                Grant access
              </Typography>
              {toGrant.map((f) => (
                <Typography key={f.id} variant="body2" sx={{ py: 0.25 }}>
                  {getFolderDisplayName(f)}
                </Typography>
              ))}
            </Box>
          )}
          {toRevoke.length > 0 && (
            <Box>
              <Typography
                variant="caption"
                fontWeight={700}
                color="error.main"
                textTransform="uppercase"
                display="block"
                mb={0.75}
              >
                Revoke access
              </Typography>
              {toRevoke.map((f) => (
                <Typography key={f.id} variant="body2" sx={{ py: 0.25 }}>
                  {getFolderDisplayName(f)}
                </Typography>
              ))}
            </Box>
          )}
          {saveError && (
            <Typography variant="caption" color="error" sx={{ mt: 1, display: "block" }}>
              Failed to save. Please try again.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCancelConfirm} disabled={isSaving} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={isSaving}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {isSaving ? "Saving..." : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
